import type { Container, Texture } from 'pixi.js';
import { Assets, Sprite, TilingSprite } from 'pixi.js';
import { viewport } from './Graphics';
import { layers } from './Layers';
import type { LoadedTextureName, PropOverrides, SpriteProperties, TextureConfig } from './types/Textures';

/** Maps texture configuration names to their rendering properties. */
const textureByName = {
  hudHealth_shadow: {
    texture: 'hud_shadow',
    layer: 'ui0',
    pivot: [330, 174],
    position: 'screencenter',
    alpha: 0.2,
  },
  hudHealth_mask: {
    texture: 'hud_mask',
    layer: 'hudHealth',
    pivot: [330, 174],
    position: 'screencenter',
  },
  hudHealth: {
    texture: 'hud',
    layer: 'hudHealth',
    pivot: [330, 174],
    position: 'screencenter',
  },
  hudEnergy_shadow: {
    texture: 'hud_shadow',
    layer: 'ui0',
    pivot: [330, 174],
    position: 'screencenter',
    alpha: 0.2,
    rotation: Math.PI,
  },
  hudEnergy_mask: {
    texture: 'hud_mask',
    layer: 'hudEnergy',
    pivot: [330, 174],
    position: 'screencenter',
    rotation: Math.PI,
  },
  hudEnergy: {
    texture: 'hud',
    layer: 'hudEnergy',
    pivot: [330, 174],
    position: 'screencenter',
    tint: 0x337ee5,
  },
  minimap: {
    texture: 'ui_minimap',
    layer: 'ui0',
    anchor: [1, 1],
    alpha: 0.25,
  },
  minimapBox: {
    texture: 'ui_minimap_box',
    layer: 'ui4',
    anchor: [0.5, 0.5],
    alpha: 0.1,
  },
  /** "You are here" dot — pure white sprite drawn over the see-through viewport
   *  box so the player can pinpoint their exact location among the team-colored
   *  mob dots. */
  minimapSelf: {
    texture: 'ui_minimap_self',
    layer: 'ui4',
    anchor: [0.5, 0.5],
    alpha: 1,
    scale: 0.45,
  },
  minimapMob: {
    texture: 'ui_minimap_mob',
    layer: 'ui1',
    anchor: [0.5, 0.5],
    alpha: 0.5,
    scale: 0.8,
  },
  /**
   * Ring drawn around the currently tracked player's minimap dot
   * (uiState.trackedPlayerId). Reuses the existing powerup_circle sprite so we
   * don't grow the spritesheet; scale and alpha keep it subtle enough to not
   * clutter the minimap.
   */
  minimapTracked: {
    texture: 'powerup_circle',
    layer: 'ui1',
    anchor: [0.5, 0.5],
    alpha: 0.7,
    scale: 0.12,
  },
  minimapBlue: {
    texture: 'ui_minimap_blue',
    layer: 'ui1',
    anchor: [0.5, 0.5],
    alpha: 0.5,
    scale: 0.8,
  },
  minimapBadgeGold: {
    texture: 'badge_gold',
    layer: 'ui1',
    anchor: [0.5, 0.5],
    scale: 0.2,
  },
  minimapBadgeSilver: {
    texture: 'badge_silver',
    layer: 'ui1',
    anchor: [0.5, 0.5],
    scale: 0.2,
  },
  minimapBadgeBronze: {
    texture: 'badge_bronze',
    layer: 'ui1',
    anchor: [0.5, 0.5],
    scale: 0.2,
  },
  minimapFlagBlue: {
    texture: 'ui_minimap_flag_blue',
    layer: 'ui3',
    anchor: [0.5, 0.5],
    scale: 0.5,
  },
  minimapFlagRed: {
    texture: 'ui_minimap_flag_red',
    layer: 'ui3',
    anchor: [0.5, 0.5],
    scale: 0.5,
  },
  minimapBaseBlue: {
    texture: 'ui_minimap_base_blue',
    layer: 'ui2',
    anchor: [0.5, 0.5],
    scale: 0.5,
  },
  minimapBaseRed: {
    texture: 'ui_minimap_base_red',
    layer: 'ui2',
    anchor: [0.5, 0.5],
    scale: 0.5,
  },
  thrusterShadow: {
    texture: 'afterburner_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.1],
  },
  missile: {
    texture: 'missile',
    layer: 'projectiles',
    anchor: [0.5, 0],
    scale: [0.2, 0.15],
  },
  missileFat: {
    texture: 'missile_fat',
    layer: 'projectiles',
    anchor: [0.5, 0],
    scale: [0.2, 0.2],
  },
  missileSmall: {
    texture: 'missile_small',
    layer: 'projectiles',
    anchor: [0.5, 0],
    scale: [0.2, 0.2],
  },
  missileShadow: {
    texture: 'missile_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.25],
  },
  missileThruster: {
    texture: 'afterburner',
    layer: 'thrusters',
    anchor: [0.5, 0.1],
    scale: [0.15, 0.15],
  },
  thrusterGlowSmall: {
    texture: 'glowsmall_copy',
    layer: 'glows',
    anchor: [0.5, 0.5],
    blend: 'add',
  },
  smokeGlow: {
    texture: 'glowdirectional',
    layer: 'glows',
    anchor: [0.5, 0.3],
    blend: 'add',
  },
  crateShadow: {
    texture: 'crate_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.5],
  },
  crateUpgrade: {
    texture: 'crate_upgrade',
    layer: 'crates',
    anchor: [0.5, 0.5],
  },
  crateShield: {
    texture: 'crate_shield',
    layer: 'crates',
    anchor: [0.5, 0.5],
  },
  crateRampage: {
    texture: 'crate_rampage',
    layer: 'crates',
    anchor: [0.5, 0.5],
  },
  powerupCircle: {
    texture: 'powerup_circle',
    layer: 'powerups',
    anchor: [0.5, 0.5],
    blend: 'add',
  },
  powerupShield: {
    texture: 'powerup_shield',
    layer: 'powerups',
    anchor: [0.5, 0.5],
    blend: 'add',
  },
  powerupRampage: {
    texture: 'powerup_rampage',
    layer: 'powerups',
    anchor: [0.5, 0.5],
    blend: 'add',
  },
  ctfFlagRed: {
    texture: 'ctf_flag_red',
    layer: 'flags',
    anchor: [15 / 128, 119 / 128],
  },
  ctfFlagBlue: {
    texture: 'ctf_flag_blue',
    layer: 'flags',
    anchor: [15 / 128, 119 / 128],
  },
  ctfFlagShadow: {
    texture: 'ctf_flag_shadow',
    layer: 'shadows',
    anchor: [3 / 32, 23 / 32],
  },
  ctfArrowBlue: {
    texture: 'arrow_indicator',
    layer: 'ui3',
    anchor: [0.5, 0.5],
    scale: 0.2,
    alpha: 0.7,
    tint: 0x5555ff,
  },
  ctfArrowRed: {
    texture: 'arrow_indicator',
    layer: 'ui3',
    anchor: [0.5, 0.5],
    scale: 0.2,
    alpha: 0.7,
    tint: 0xff0000,
  },
  shipRaptor: {
    texture: 'raptor',
    layer: 'aircraft',
    anchor: [0.5, 0.6],
  },
  shipRaptorShadow: {
    texture: 'raptor_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.57],
  },
  shipRaptorThruster: {
    texture: 'afterburner',
    layer: 'thrusters',
    anchor: [0.5, 0.1],
    scale: [0.25, 0.25],
  },
  shipSpirit: {
    texture: 'spirit',
    layer: 'aircraft',
    anchor: [0.5, 0.5],
  },
  shipSpiritShadow: {
    texture: 'spirit_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.5],
  },
  shipSpiritThruster: {
    texture: 'afterburner',
    layer: 'thrusters',
    anchor: [0.5, 0.1],
    scale: [0.25, 0.25],
  },
  shipComanche: {
    texture: 'comanche',
    layer: 'aircraft',
    anchor: [0.5, 0.4],
  },
  shipComancheShadow: {
    texture: 'comanche_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.43],
  },
  shipComancheRotor: {
    texture: 'comanche_rotor',
    layer: 'aircraft',
    anchor: [0.5, 0.5],
    scale: [0.25, 0.25],
  },
  shipComancheRotorShadow: {
    texture: 'comanche_rotor_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.5],
  },
  shipTornado: {
    texture: 'tornado',
    layer: 'aircraft',
    anchor: [0.5, 0.65],
  },
  shipTornadoShadow: {
    texture: 'tornado_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.605],
  },
  shipProwler: {
    texture: 'prowler',
    layer: 'aircraft',
    anchor: [0.5, 0.5],
  },
  shipProwlerShadow: {
    texture: 'prowler_shadow',
    layer: 'shadows',
    anchor: [0.5, 0.5],
  },
  mountain1: {
    texture: 'mountain1',
    layer: 'doodads',
    anchor: [0.5, 0.5],
  },
  mountain2: {
    texture: 'mountain2',
    layer: 'doodads',
    anchor: [0.5, 0.5],
  },
  mountain3: {
    texture: 'mountain3',
    layer: 'doodads',
    anchor: [0.5, 0.5],
  },
  mountain4: {
    texture: 'mountain4',
    layer: 'doodads',
    anchor: [0.5, 0.5],
  },
  doodadField: {
    texture: 'doodad_field',
    layer: 'fields',
    anchor: [0.5, 0.5],
  },
} satisfies Record<string, TextureConfig>;

/** Texture configuration names derived from textureByName keys. */
export type TextureConfigName = keyof typeof textureByName;

/** Gets a texture from the PixiJS Assets cache, throws if not found. */
export function getTexture(name: LoadedTextureName) {
  const texture = Assets.get<Texture>(name);
  if (!texture) {
    throw new Error(
      `Texture "${name}" not found in Assets cache. ` +
        `Ensure Assets.loadBundle('game-core') completed before accessing textures.`,
    );
  }
  return texture;
}

/** Gets a texture by name, undefined when the bundle has no such key (Pixi logs the miss). */
export function get(name: LoadedTextureName): Texture | undefined {
  return Assets.get<Texture>(name);
}

/** Gets a texture by its configuration name. */
export function getNamed(configName: TextureConfigName) {
  return getTexture(textureByName[configName].texture);
}

/** Creates a sprite from a texture configuration, with optional property overrides. */
export function init(textureName: TextureConfigName, propOverrides?: PropOverrides) {
  // Overrides go into a fresh object so the table stays untouched
  const textureConfig: TextureConfig = propOverrides
    ? { ...textureByName[textureName], ...propOverrides }
    : textureByName[textureName];

  // Convert "screencenter" to actual screen coordinates
  let resolvedPosition: [number, number] | undefined;
  if (textureConfig.position === 'screencenter') {
    resolvedPosition = [viewport.halfWidth, viewport.halfHeight];
  } else if (Array.isArray(textureConfig.position)) {
    resolvedPosition = textureConfig.position;
  }

  const layer = layers[textureConfig.layer];

  let texture: Texture;
  try {
    texture = getTexture(textureConfig.texture);
  } catch (error) {
    throw new Error(`Texture "${textureConfig.texture}" for config "${textureName}" not found.`, { cause: error });
  }

  // Sprite properties, resolved to numbers only
  return buildSprite(texture, layer, {
    position: resolvedPosition,
    anchor: textureConfig.anchor,
    pivot: textureConfig.pivot,
    scale: textureConfig.scale,
    rotation: textureConfig.rotation,
    alpha: textureConfig.alpha,
    blend: textureConfig.blend,
    tint: textureConfig.tint,
    visible: textureConfig.visible,
  });
}

/** Creates a sprite by texture name with the given properties, added to the container. */
export function initSprite(name: LoadedTextureName, container: Container, properties: SpriteProperties) {
  return buildSprite(getTexture(name), container, properties);
}

/** Applies the properties to a new sprite of the texture and adds it to the container. */
function buildSprite(texture: Texture, container: Container, properties: SpriteProperties) {
  const spriteObj = new Sprite(texture);

  // Apply position
  if (properties.position) {
    spriteObj.position.set(properties.position[0], properties.position[1]);
  }

  // Apply anchor
  if (properties.anchor) {
    spriteObj.anchor.set(properties.anchor[0], properties.anchor[1]);
  }

  // Apply pivot
  if (properties.pivot) {
    spriteObj.pivot.set(properties.pivot[0], properties.pivot[1]);
  }

  // Apply scale (can be uniform or per-axis)
  if (properties.scale) {
    if (Array.isArray(properties.scale)) {
      spriteObj.scale.set(properties.scale[0], properties.scale[1]);
    } else {
      spriteObj.scale.set(properties.scale);
    }
  }

  // Apply rotation
  if (properties.rotation) {
    spriteObj.rotation = properties.rotation;
  }

  // Apply alpha
  if (properties.alpha) {
    spriteObj.alpha = properties.alpha;
  }

  // Apply blend mode
  if (properties.blend) {
    spriteObj.blendMode = properties.blend;
  }

  // Apply tint
  if (properties.tint) {
    spriteObj.tint = properties.tint;
  }

  // Apply visibility
  if (properties.visible !== undefined) {
    spriteObj.visible = properties.visible;
  }

  // Add to container and return
  container.addChild(spriteObj);
  return spriteObj;
}

/** Creates a new Sprite from a texture name. */
export function sprite(name: LoadedTextureName) {
  return new Sprite(getTexture(name));
}

/** Creates a new TilingSprite from a texture name. */
export function tile(name: LoadedTextureName, width: number, height: number) {
  return new TilingSprite({ texture: getTexture(name), width, height });
}
